import axios from 'axios';
import i18next from 'i18next';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fileNotFound, notresultsfound } from '../../../../assets/images/icons';
import { useRefresh } from '../../../../utils/RefreshProvider';
import { useParams } from 'react-router';
import { ArrowLeftIcon, StarIcon } from '@heroicons/react/24/solid';
import EditStock from './EditStock';
import DeleteElement from '../components/DeleteElement';

export default function StockManage() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const token = localStorage.getItem("token");
  const localhost = process.env.REACT_APP_LOCALHOST;
  const localimg = process.env.REACT_APP_LOCALHOST_IMG;
  const { refreshKey } = useRefresh();
  const [stocks, setStocks] = useState([]);
  const [productData, setProductData] = useState(null);
  const [loading, setloading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${localhost}admin/product-stocks/${slug}`, { headers: { Authorization: `Bearer ${token}` } });
        setStocks(res.data.stock_data);

        const updatedData = res.data.product_data;
        updatedData.imgs = JSON.parse(updatedData.imgs);
        setProductData(updatedData);
      } catch (err) {
        console.error(err);
      } finally {
        setloading(false);
      }
    };

    fetchData();
  }, [refreshKey]);

  const totalAmount = stocks.reduce((sum, item) => sum + parseInt(item.stock), 0);

  const ProductInfoCard = ({ product }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6 mb-6">
      <div className="flex flex-col md:flex-row gap-6">
        {/* Product Image */}
        <div className="w-full md:w-1/4">
          <div className="relative aspect-square rounded-lg overflow-hidden bg-gray-100">
            <img src={product.imgs?localimg + product.imgs[0]:fileNotFound} alt={product.slug} className="object-cover w-full h-full hover:scale-105 transition-transform duration-300" />
          </div>
        </div>

        {/* Product Details */}
        <div className="w-full md:w-3/4 space-y-4 flex flex-col justify-between">
          <div>
            <div className="flex items-start">
              <div>
                <a href={`/product/${product.slug}`} className="text-xl font-bold text-black mb-1 hover:underline">
                  {product.name[i18next.language]}
                </a>
                <p className="text-sm text-gray-500">#{product.num_product}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 capitalize mt-4">
              <div className="bg-orange-50 p-4 rounded-lg">
                <p className="text-sm text-orange-600 mb-1">{t("price")}</p>
                <p className="font-semibold">{t(product.price)} MAD</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <p className="text-sm text-orange-600 mb-1">{t("rating")}</p>
                <p className="font-semibold flex flex-wrap gap-1 items-center">
                  {t(product.ratings)}
                  <StarIcon className="size-4 pb-0.5" />
                </p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <p className="text-sm text-orange-600 mb-1">{t("gender")}</p>
                <p className="font-semibold">{t(product.gender)}</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <p className="text-sm text-orange-600 mb-1">{t("category")}</p>
                <p className="font-semibold">{t(product.category_name[i18next.language])}</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <p className="text-sm text-orange-600 mb-1">{t("stock-total")}</p>
                <p className="font-semibold">{totalAmount ? totalAmount : "error"}</p>
              </div>
            </div>
          </div>

          {/* Edit Button */}
          <div className="flex justify-start mt-4">
            <a href={`/admin/update-product/${product.slug}`} className="text-sm bg-black hover:bg-black/80 text-white rounded-md py-1.5 px-3">
              {t("edit")}
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  // Mobile Card Component
  const MobileCard = ({ item }) => (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <div className="flex justify-between items-start mb-3">
        <div>
          <span className="text-xs text-gray-500 block mb-1">Stock ID</span>
          <span className="font-semibold text-black"># {item.id}</span>
        </div>
        <div className="flex gap-2">
          {/*  */}
        </div>
      </div>
      <dl className="space-y-3">
        <div>
          <dt className="text-xs text-gray-500 mb-1">{t("size")}</dt>
          <dd className="text-sm text-black">{item.size_name}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500 mb-1">{t("color")}</dt>
          <dd className="text-sm text-black">
            {
              item.color_name ? (
                <div data-tooltip-id="my-tooltip" data-tooltip-content={item.color_name[i18next.language]}
                  className='border border-gray-300 size-8 rounded-full' style={{ background: item.color_hex }} />
              ) : "--"
            }
          </dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500 mb-1">{t("stock")}</dt>
          <dd className="text-sm text-black">{item.stock}</dd>
        </div>
      </dl>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-black">{t("stock-management")}</h1>
        <a href="/admin/products" className="flex items-center gap-1 text-sm bg-black hover:bg-black/80 text-white px-3 py-2 rounded-md">
          <ArrowLeftIcon className="h-5 w-auto" />
          <span>{t("back-to-products")}</span>
        </a>
      </div>

      {/* Content Section */}
      {loading ? (
        <div className="min-h-[55vh] flex items-center justify-center">
          <div className="loading-spinner" />
        </div>
      ) : stocks.length > 0 ? (
        <>
          {/* Product Info Card */}
          {productData && <ProductInfoCard product={productData} />}

          <div className="border border-gray-200 hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {["Stock ID", "size", "color", "stock", "actions"].map((item, index) => (
                      <th key={index} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t(item)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {stocks.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-black"># {item.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{item.size_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {
                          item.color_name ? (
                            <div data-tooltip-id="my-tooltip" data-tooltip-content={item.color_name[i18next.language]}
                              className='border border-gray-300 size-8 rounded-full' style={{ background: item.color_hex }} />
                          ) : "--"
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{item.stock}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <EditStock stockId={item.id} />
                        <DeleteElement typeProp="stock" elementId={item.id} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="md:hidden grid gap-4">
            {stocks.map((item, index) => (
              <MobileCard key={index} item={item} />
            ))}
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-24 bg-white rounded-lg shadow-sm">
          <img loading="lazy" src={notresultsfound} alt="No results found" className="w-40 h-auto mb-4" />
        </div>
      )}
    </div>
  );
}